import { NUMBER_STATUS } from 'countable@helpers';
import { AmorCalculationModel, AmortizationInputsModel, AmortizationScheduleModel } from 'countable@model';

export class CalculationsUtil {
  static formatScheduleRows(scheduleRows: AmortizationScheduleModel[]) {
    scheduleRows && scheduleRows?.forEach(r => {
      r.closingBal = this.formatNumber(r?.closingBal);
      r.openingBalTb = this.formatNumber(r?.openingBalTb);
      r.amortization = this.formatNumber(r?.amortization);
      r.closingBalTb = this.formatNumber(r?.closingBalTb);
      r.currYear = this.formatNumber(r?.currYear);
      r.prevYear = this.formatNumber(r?.prevYear);
      r.balTb = this.formatNumber(r?.balTb);
      r.underOver = this.formatNumber(r?.underOver);
      r.openingBalOne = this.formatNumber(r?.openingBalOne);
      r.addAdj = this.formatNumber(r?.addAdj);
      r.disAdj = this.formatNumber(r?.disAdj);
    });
    return structuredClone(scheduleRows);
  }

  static formatNumber(value: any) {
    if (!value) {
      return '0.00';
    }
    if (typeof value === 'string' && value.includes(',')) {
      return value.replace(/,/g, '');
    }
    if (typeof value === 'string' && value.includes('(') && value.includes(')')) {
      value = value.replace(/[()]/g, '');
      value = `-${value}`;
    }
    return value;
  }

  static getUpdatedTotalAndRowValue(total: any, rowValue: any, isConvertNumber: boolean = false) {
    const formattedTotal = this.formatNumber(total);
    let updatedRowValue = Number(parseFloat(rowValue).toFixed(2)).toLocaleString('en-CA', {
      minimumFractionDigits: 2
    });
    if (isConvertNumber) {
      updatedRowValue = updatedRowValue.replace('-', '(') + updatedRowValue.substring(updatedRowValue?.length) + ')';
    }
    return [this.convertValue((Number(parseFloat(formattedTotal)?.toFixed(2)) + parseFloat(rowValue))), updatedRowValue];
  }

  static convertValue(value: any) {
    let updatedVal = value?.toLocaleString('en-CA', {
      minimumFractionDigits: 2
    });
    const valStatus = this.statusOfValue(value);
    if (valStatus === 'NOT_NUMBER') {
      return '0.00';
    }
    if (valStatus === 'NEGATIVE') {
      return updatedVal.replace('-', '(') + updatedVal.substring(updatedVal?.length) + ')';
    }
    return updatedVal;
  }

  static statusOfValue(input: any): NUMBER_STATUS {
    const val = Math.sign(parseFloat(this.formatNumber(input)));
    if (isNaN(val)) {
      return 'NOT_NUMBER';
    }
    if (val === 1 || val === 0) {
      return 'POSITIVE';
    }
    if (val === -1) {
      return 'NEGATIVE';
    }
  };

  static calculateDifference(value1: string, value2: string): number {
    const num1 = this.parseValue(value1);
    const num2 = this.parseValue(value2);
    return this.transformValue(Math.abs(num1 - num2));
  }

  static parseValue(value: string): number {
    if ((value?.includes('(') || value?.includes(')')) && value?.startsWith('(') && value?.endsWith(')')) {
      value = value.slice(1, -1); // Remove the parentheses
      return -parseFloat(value.replace(/,/g, ''));
    }
    return parseFloat(value.replace(/,/g, ''));
  }

  static transformValue(value: any) {
    if (isNaN(value)) {
      return value;
    }
    const absoluteValue = Math.abs(value);
    const formattedValue = absoluteValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    if (value < 0) {
      return `(${formattedValue})`;
    }
    return formattedValue;
  }

  static getRowInfo(rowType: 'TB' | 'TOTAL' | 'Difference' | 'Balance' | 'Total') {
    const totalRow = new AmortizationScheduleModel();
    totalRow.rowType = rowType;
    totalRow.description = rowType;
    return structuredClone(totalRow);
  }

  static calculateDiffBtwTotalAndGL(calTotalRow: AmortizationScheduleModel, balanceRow: AmortizationScheduleModel) {
    const diffRow = new AmortizationScheduleModel();
    diffRow.description = 'Difference';
    diffRow.rowType = 'Difference';
    diffRow.openingBalOne = this.calculateDifference(calTotalRow?.openingBalOne?.toString(), balanceRow?.openingBalOne?.toString());
    diffRow.closingBal = this.calculateDifference(calTotalRow?.closingBal?.toString(), balanceRow?.closingBal?.toString());
    diffRow.openingBalTb = this.calculateDifference(calTotalRow?.openingBalTb?.toString(), balanceRow?.openingBalTb?.toString());
    diffRow.closingBalTb = this.calculateDifference(calTotalRow?.closingBalTb?.toString(), balanceRow?.closingBalTb?.toString());
    diffRow.currYear = this.calculateDifference(calTotalRow?.currYear?.toString(), balanceRow?.currYear?.toString());
    diffRow.prevYear = this.calculateDifference(calTotalRow?.prevYear?.toString(), balanceRow?.prevYear?.toString());
    diffRow.addAdj = '';
    diffRow.disAdj = '';
    diffRow.amortization = '';
    diffRow.balTb = '';
    diffRow.underOver = '';
    return diffRow;
  }

  static parseAndReturnAbs(input: string | number) {
    if (input === 0 || input === '0.00' || !input) {
      return 0;
    }
    if (typeof input === 'number') {
      return input;
    }
    let cleanedInput = input.replace(/,/g, '');
    if (cleanedInput.startsWith('(') && cleanedInput.endsWith(')')) {
      cleanedInput = '-' + cleanedInput.slice(1, -1);
    }
    return Math.abs(parseFloat(cleanedInput));
  }

  static performCalculation(valueA: any, valueB: any, type: 'ADD' | 'SUB' = 'SUB'): string {
    function parseNumber(input: any): number {
      let cleanedInput = input.replace(/,/g, '');
      if (cleanedInput.startsWith('(') && cleanedInput.endsWith(')')) {
        cleanedInput = '-' + cleanedInput.slice(1, -1);
      }
      return parseFloat(cleanedInput);
    }
    const valueANum = parseNumber(valueA);
    const valueBNum = parseNumber(valueB);
    let result = 0;
    if (type === 'ADD') {
      result = valueANum + valueBNum;
    } else if (type === 'SUB') {
      result = valueANum - valueBNum;
    }
    return result < 0
      ? `(${Math.abs(result).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})})`
      : result.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
  }

  static formatToNorthAmericanStandard(value: number, isDisposition: boolean = false): string {
    if (isNaN(value) || !value) {
      return '0.00';
    }
    const roundedValue = Number(value).toFixed(2);
    const isNegative = Number(roundedValue) < 0;
    const formattedValue = Math.abs(Number(roundedValue))
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (isNegative || isDisposition) ? `(${formattedValue})` : formattedValue;
  }

  static calculateAmortizationForGL(detail: AmorCalculationModel, method: string) {
    switch (method) {
      case 'Straight line':
        return this.formatToNorthAmericanStandard(this.calculateSL({...detail}), detail?.rowType === 'DIS');
      case 'Decline':
        return this.formatToNorthAmericanStandard(this.calculateDecline({...detail}), detail?.rowType === 'DIS');
      case 'Double Decline':
        return this.formatToNorthAmericanStandard(this.calculateDoubleDecline({...detail}), detail?.rowType === 'DIS');
      case 'Units of Production':
        return this.formatToNorthAmericanStandard(this.calculateUOP({...detail}), detail?.rowType === 'DIS');
      case 'Sum Of Years Digit':
        return this.formatToNorthAmericanStandard(this.calculateSOY({...detail}), detail?.rowType === 'DIS');
    }
  }

  static calculateSL(detail: AmorCalculationModel) {
    const openingBal = this.parseAndReturnAbs(detail?.amount);
    const residualValue = this.parseAndReturnAbs(detail?.residualValue);
    const usefulLife = this.parseAndReturnAbs(detail?.usefulLife);
    const months = this.parseAndReturnAbs(detail?.monthInUse);
    const depreciation = (openingBal - residualValue) / usefulLife;
    return (depreciation * (months / 12));

  }

  static calculateDecline(detail: AmorCalculationModel) {
    const openingBal = this.parseAndReturnAbs(detail?.amount);
    const residualValue = this.parseAndReturnAbs(detail?.residualValue);
    const rate = this.parseAndReturnAbs(detail?.rate);
    const months = this.parseAndReturnAbs(detail?.monthInUse);
    const bookValue = openingBal - residualValue;
    const depreciation = bookValue * (rate / 100);
    return (depreciation * (months / 12));
  }

  static calculateDoubleDecline(detail: AmorCalculationModel) {
    const openingBal = this.parseAndReturnAbs(detail?.amount);
    const residualValue = this.parseAndReturnAbs(detail?.residualValue);
    const rate = this.parseAndReturnAbs(detail?.rate);
    const months = this.parseAndReturnAbs(detail?.monthInUse);
    const usefulLife = this.parseAndReturnAbs(detail?.usefulLife);
    const bookValue = openingBal - residualValue;
    const depRate = 2 / usefulLife;
    const depreciation = bookValue * depRate;
    return (depreciation * (months / 12));
  }

  static calculateUOP(detail: AmorCalculationModel) {
    const openingBal = this.parseAndReturnAbs(detail?.amount);
    const residualValue = this.parseAndReturnAbs(detail?.residualValue);
    const estimatedProduction = this.parseAndReturnAbs(detail?.estimatedProduction);
    const unitsProduced = this.parseAndReturnAbs(detail?.unitsProduced);
    const depByUnit = (openingBal - residualValue) / estimatedProduction;
    return depByUnit * unitsProduced;
  }

  static calculateSOY(detail: AmorCalculationModel) {
    const openingBal = this.parseAndReturnAbs(detail?.amount);
    const residualValue = this.parseAndReturnAbs(detail?.residualValue);
    const usefulLife = this.parseAndReturnAbs(detail?.usefulLife);
    const remainingLife = this.parseAndReturnAbs(detail?.remainingLife);
    const months = this.parseAndReturnAbs(detail?.monthInUse);
    const soy = (usefulLife * (usefulLife + 1)) / 2;
    const depreciation = (remainingLife / soy) * (openingBal - residualValue);
    return depreciation * (months / 12);
  }

  static calculateAmortization(rows: AmorCalculationModel[]) {
    const openingBalance = this.parseAndReturnAbs(rows?.filter(r => r?.rowType === 'OPENING')[0]?.amortization);
    const totalAdditions = this.calculateTotal(rows?.filter(r => r?.rowType === 'ADD')
      ?.map(a => this.parseAndReturnAbs(a?.amortization)));
    const totalDispositions = this.calculateTotal(rows?.filter(r => r?.rowType === 'DIS')
      ?.map(d => this.parseAndReturnAbs(d?.amortization)));
    return this.formatToNorthAmericanStandard((totalAdditions + openingBalance) - totalDispositions);
  }

  static calculateTotal(transactions: number[]) {
    return transactions.reduce((total, num) => {
      return total + num;
    }, 0);
  }

}
